import { useState, useMemo } from 'react'
import {
  getBelt, getTheme, isChampion, calcRating, calcCatScore,
  makeAvatarSvg, makeInitialsSvg, getFlagSvg,
  getBeltLevel, getBeltLevelProgress, getSessionsToNextLevel, getStripeCount
} from '../lib/helpers'
import { FRONT_STAT_KEYS } from '../lib/constants'

export default function FighterCard({ fighter, skills, competitions, categories, techniques, size = 'normal', onClick }) {
  const [flipped, setFlipped] = useState(false)
  const belt = getBelt(fighter)
  const theme = getTheme(fighter, competitions)
  const rating = useMemo(() => calcRating(fighter, skills, competitions), [fighter, skills, competitions])
  const level = useMemo(() => getBeltLevel(fighter), [fighter])
  const levelProgress = useMemo(() => getBeltLevelProgress(fighter), [fighter])
  const stripes = useMemo(() => getStripeCount(fighter, level), [fighter, level])
  const champion = useMemo(() => isChampion(fighter, competitions), [fighter, competitions])

  const frontStats = useMemo(() => {
    return FRONT_STAT_KEYS.map(({ label, cat }) => {
      const catData = categories?.find(c => c.name === cat)
      const techIds = catData ? techniques?.filter(t => t.category_id === catData.id).map(t => t.id) : []
      return { label, value: calcCatScore(skills, techIds) }
    })
  }, [skills, categories, techniques])

  const backStats = useMemo(() => {
    return (categories || []).map(cat => {
      const techIds = techniques?.filter(t => t.category_id === cat.id).map(t => t.id) || []
      return { name: cat.name, value: calcCatScore(skills, techIds) }
    })
  }, [skills, categories, techniques])

  const faceHtml = fighter.photo_url ? null
    : fighter.avatar_style === 'initials'
      ? makeInitialsSvg(fighter.first_name, fighter.last_name, belt.a1, belt.a2, 200)
      : makeAvatarSvg(fighter.avatar_seed || 1, belt.a1, belt.a2, 200)

  const flagSvg = getFlagSvg(fighter.country || 'HR')
  const fighterComps = competitions?.filter(c => c.fighter_id === fighter.id) || []
  const goldCount = fighterComps.filter(c => c.medal === 'gold').length
  const silverCount = fighterComps.filter(c => c.medal === 'silver').length
  const bronzeCount = fighterComps.filter(c => c.medal === 'bronze').length

  const w = size === 'small' ? 160 : 200
  const h = size === 'small' ? 260 : 320

  const handleClick = () => { setFlipped(f => !f); onClick && onClick(fighter) }

  // Level 10 ready for promotion glow
  const promotionReady = level >= 10

  return (
    <div onClick={handleClick} style={{ width: w, height: h, perspective: 900, cursor: 'pointer', flexShrink: 0 }}>
      <div style={{
        width: '100%', height: '100%', position: 'relative',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.55s cubic-bezier(0.4,0,0.2,1)',
        transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
      }}>

        {/* FRONT */}
        <div style={{
          position: 'absolute', width: '100%', height: '100%', borderRadius: 14,
          overflow: 'hidden', backfaceVisibility: 'hidden',
          background: theme.bg,
          border: promotionReady ? `3px solid #f0c010` : `2.5px solid ${theme.border}`,
          color: theme.color,
          boxShadow: promotionReady ? `0 0 20px rgba(240,192,16,0.6)` : 'none',
          display: 'flex', flexDirection: 'column',
        }}>
          <Shine />
          {promotionReady && <PromotionGlow />}

          {/* Left shade */}
          <div style={{ position: 'absolute', top: 0, left: 0, width: '38%', height: '100%', background: 'linear-gradient(to right, rgba(0,0,0,0.12), transparent)', zIndex: 1, pointerEvents: 'none' }} />
          {/* Photo fills card */}
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            {fighter.photo_url
              ? <img src={fighter.photo_url} alt={fighter.first_name} style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'top center', display: 'block' }} />
              : <div dangerouslySetInnerHTML={{ __html: faceHtml }} style={{ width: '100%', height: '100%' }} />
            }
            {/* Rating top-left overlay */}
            <div style={{ position: 'absolute', top: 6, left: 6, textAlign: 'center', lineHeight: 1, zIndex: 3 }}>
              <div style={{ fontSize: w * 0.19, fontWeight: 900, letterSpacing: '-1px', fontFamily: "'VT323', monospace", lineHeight: 1 }}>{rating}</div>
              <div style={{ fontSize: 9, opacity: 0.85, fontFamily: "'VT323', monospace" }}>BJJ</div>
              <div style={{ fontSize: 14, margin: '2px auto' }}>{(fighter.country||'HR')==='HR'?'🇭🇷':(fighter.country||'HR')==='DE'?'🇩🇪':(fighter.country||'HR')==='US'?'🇺🇸':(fighter.country||'HR')==='GB'?'🇬🇧':'🏳️'}</div>
            </div>
          </div>

          {/* Bottom info */}
          <div style={{ padding: '5px 8px 4px' }}>
            <div style={{ fontSize: 13, fontWeight: 900, textAlign: 'center', letterSpacing: '0.06em', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: "'VT323', monospace" }}>
              {fighter.first_name.toUpperCase()} {fighter.last_name.toUpperCase()}
            </div>

            {/* Belt + level */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3, justifyContent: 'center' }}>
              <span style={{ fontSize: 8, opacity: 0.75, whiteSpace: 'nowrap' }}>{belt.short}</span>
              {stripes > 0 && (
                <span style={{ display: 'inline-flex', gap: 1 }}>
                  {Array(stripes).fill(0).map((_, i) => (
                    <span key={i} style={{ width: 3, height: 7, background: 'currentColor', opacity: 0.8, borderRadius: 1 }} />
                  ))}
                </span>
              )}
              <span style={{ fontSize: 8, opacity: 0.5 }}>·</span>
              <span style={{ fontSize: 8, opacity: 0.75 }}>Lv.{level}</span>
              {promotionReady && <span style={{ fontSize: 7, background: 'rgba(240,192,16,0.3)', border: '1px solid rgba(240,192,16,0.6)', borderRadius: 4, padding: '0px 3px', color: theme.color }}>READY</span>}
            </div>

            {/* Level progress bar */}
            <div style={{ height: 3, background: 'rgba(0,0,0,0.2)', borderRadius: 2, marginBottom: 4, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, levelProgress * 100)}%`, height: '100%', background: 'currentColor', opacity: 0.7, borderRadius: 2, transition: 'width 0.3s' }} />
            </div>

            <div style={{ height: '0.5px', background: 'currentColor', opacity: 0.2, marginBottom: 4 }} />

            {/* Stats */}
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              {frontStats.map(s => (
                <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 900, lineHeight: 1, fontFamily: "'VT323', monospace" }}>{s.value}</span>
                  <span style={{ fontSize: 7, letterSpacing: '0.05em', opacity: 0.65, textTransform: 'uppercase', fontFamily: "'VT323', monospace" }}>{s.label}</span>
                </div>
              ))}
            </div>

            {/* Medal pips */}
            {(goldCount + silverCount + bronzeCount) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginTop: 4 }}>
                {Array(Math.min(goldCount, 4)).fill(0).map((_, i) => <Pip key={'g'+i} color="#d4a017" />)}
                {Array(Math.min(silverCount, 3)).fill(0).map((_, i) => <Pip key={'s'+i} color="#909090" />)}
                {Array(Math.min(bronzeCount, 3)).fill(0).map((_, i) => <Pip key={'b'+i} color="#a05020" />)}
              </div>
            )}
          </div>
        </div>

        {/* BACK */}
        <div style={{
          position: 'absolute', width: '100%', height: '100%', borderRadius: 14,
          overflow: 'hidden', backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
          background: theme.bg, border: `2.5px solid ${theme.border}`, color: theme.color,
        }}>
          <Shine delay="2s" />
          <div style={{ padding: '10px 10px 8px', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textAlign: 'center', letterSpacing: '0.05em', marginBottom: 1 }}>
              {fighter.first_name.toUpperCase()} {fighter.last_name.toUpperCase()}
            </div>
            <div style={{ fontSize: 8, textAlign: 'center', opacity: 0.6, marginBottom: 2 }}>
              {belt.n}{stripes > 0 ? ' · ' + stripes + (stripes === 1 ? ' stripe' : ' stripes') : ''} · Level {level}/10 · {fighter.belt_sessions || 0} sessions at belt
            </div>
            <div style={{ fontSize: 8, textAlign: 'center', opacity: 0.5, marginBottom: 5 }}>
              OVR {rating} · Total sessions: {fighter.sessions_count || 0}
            </div>
            <div style={{ height: '0.5px', background: 'currentColor', opacity: 0.2, marginBottom: 5 }} />

            {/* Skill bars */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {backStats.map(s => (
                <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 7.5, opacity: 0.7, flex: '0 0 58px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                  <div style={{ flex: 1, height: 4, background: 'rgba(0,0,0,0.18)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: `${s.value}%`, height: '100%', background: 'currentColor', opacity: 0.75, borderRadius: 2 }} />
                  </div>
                  <span style={{ fontSize: 8, fontWeight: 600, opacity: 0.85, minWidth: 16, textAlign: 'right' }}>{s.value}</span>
                </div>
              ))}
            </div>

            {/* Comp history */}
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 7.5, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Competitions</div>
              {fighterComps.length === 0
                ? <div style={{ fontSize: 8, opacity: 0.4 }}>No competitions yet</div>
                : fighterComps.slice(-3).reverse().map((c, i) => {
                    const col = c.medal === 'gold' ? '#d4a017' : c.medal === 'silver' ? '#909090' : '#a05020'
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: col, flexShrink: 0 }} />
                        <span style={{ fontSize: 7.5, opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.tournament_name}{c.submission_used ? ' · ' + c.submission_used : ''}
                        </span>
                      </div>
                    )
                  })
              }
            </div>

            {/* Level progress on back */}
            {level < 10 && (
              <div style={{ marginTop: 6, fontSize: 8, opacity: 0.6, textAlign: 'center' }}>
                {getSessionsToNextLevel(fighter)} sessions to Level {level + 1}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Shine({ delay = '0s' }) {
  return (
    <div style={{
      position: 'absolute', top: 0, left: '-80%', width: '40%', height: '100%',
      background: 'linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.22) 50%,transparent 70%)',
      animation: `shine 4s ${delay} infinite`, pointerEvents: 'none', zIndex: 10
    }} />
  )
}

function PromotionGlow() {
  return (
    <div style={{
      position: 'absolute', inset: 0, borderRadius: 12,
      background: 'linear-gradient(135deg,rgba(240,192,16,0.08) 0%,transparent 50%,rgba(240,192,16,0.08) 100%)',
      animation: 'promotionPulse 2s ease-in-out infinite', pointerEvents: 'none', zIndex: 5
    }} />
  )
}

function Pip({ color }) {
  return <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
}
